import React, { useMemo, useRef } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import Filters from './Filters';
import DateRangeFilter from './DateRangeFilter';
import LeadTimeChartTypeSelector from './LeadTimeChartTypeSelector';
import useIsMobile from '../hooks/useIsMobile';
import { calculatePercentile, PERCENTILE_COLORS } from '../utils/chartUtils';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';

function Histogram({ data, allTypes, visibleTypes, visiblePercentiles, setVisibleTypes, togglePercentile, leadTimeChartType, setLeadTimeChartType, plausible }) {
  const isMobile = useIsMobile();
  const chartRef = useRef(null);
  
  // Estado para a escala do histograma
  const [binScale, setBinScale] = React.useState('1em1'); // '1em1', '10em10', 'dinamico'
  const scaleOptions = [
    { key: '1em1', label: '1 em 1' },
    { key: '10em10', label: '10 em 10' },
    { key: 'dinamico', label: 'Dinâmico' },
  ];
  
  // Determina datas mín/max para o filtro
  const closedDates = useMemo(() => data.map(item => new Date(item['Closed Date'])).sort((a, b) => a - b), [data]);
  const minDate = closedDates.length ? closedDates[0].toISOString().slice(0, 10) : '';
  const maxDate = closedDates.length ? closedDates[closedDates.length - 1].toISOString().slice(0, 10) : '';
  const [startDate, setStartDate] = React.useState(minDate);
  const [endDate, setEndDate] = React.useState(maxDate);

  // Atualiza datas do filtro sempre que minDate/maxDate mudarem
  React.useEffect(() => {
    setStartDate(minDate);
    setEndDate(maxDate);
  }, [minDate, maxDate]);

  // Filtra os dados conforme o período e tipos visíveis
  const filteredData = useMemo(() => data.filter(item => {
    const d = new Date(item['Closed Date']);
    const type = item['Tipo de Item'] || 'Item';
    return d >= new Date(startDate) && d <= new Date(endDate) && visibleTypes.includes(type);
  }), [data, startDate, endDate, visibleTypes]);

  const leadTimes = useMemo(() => filteredData.map(item => item.leadTime), [filteredData]);
  
  // Calcula percentis
  const percentiles = useMemo(() => ({
    P50: calculatePercentile(leadTimes, 50),
    P85: calculatePercentile(leadTimes, 85),
    P95: calculatePercentile(leadTimes, 95)
  }), [leadTimes]);

  // Função para criar bins baseado na escala selecionada
  const createBinsByScale = (leadTimes, scale) => {
    if (leadTimes.length === 0) return [];
    
    const min = Math.min(...leadTimes);
    const max = Math.max(...leadTimes);
    
    let binSize, binCount, bins;
    
    if (scale === '1em1') {
      // 1 dia por bin
      binSize = 1;
      binCount = max - min + 1;
      bins = Array.from({ length: binCount }, (_, i) => ({
        range: `${min + i}`,
        rangeStart: min + i,
        rangeEnd: min + i,
        count: 0,
        items: []
      }));
    } else if (scale === '10em10') {
      // 10 dias por bin, começando sempre de 1
      binSize = 10;
      // Garantir que sempre comece do 1
      let startBin = 1;
      // Se o mínimo for maior que 10, ajustar para o múltiplo de 10 anterior + 1
      if (min > 10) {
        startBin = Math.floor((min - 1) / binSize) * binSize + 1;
      }
      const endBin = Math.ceil(max / binSize) * binSize;
      binCount = Math.ceil((endBin - startBin + 1) / binSize);
      bins = Array.from({ length: binCount }, (_, i) => {
        const start = startBin + i * binSize;
        const end = start + binSize - 1;
        return {
          range: `${start}-${end}`,
          rangeStart: start,
          rangeEnd: end,
          count: 0,
          items: []
        };
      });
    } else { // 'dinamico'
      // Escala dinâmica baseada no intervalo de dados
      const range = max - min;
      if (range <= 20) {
        binSize = 1;
      } else if (range <= 100) {
        binSize = 5;
      } else {
        binSize = 10;
      }
      
      let startBin;
      if (binSize === 1) {
        startBin = min;
      } else {
        // Para bins de 5 ou 10, começar sempre de 1 ou múltiplo + 1
        startBin = 1;
        if (min > binSize) {
          startBin = Math.floor((min - 1) / binSize) * binSize + 1;
        }
      }
      
      const endBin = binSize === 1 ? max : Math.ceil(max / binSize) * binSize;
      binCount = binSize === 1 ? (max - min + 1) : Math.ceil((endBin - startBin + 1) / binSize);
      
      bins = Array.from({ length: binCount }, (_, i) => {
        const start = startBin + i * binSize;
        const end = binSize === 1 ? start : start + binSize - 1;
        return {
          range: binSize === 1 ? `${start}` : `${start}-${end}`,
          rangeStart: start,
          rangeEnd: end,
          count: 0,
          items: []
        };
      });
    }

    // Distribui os lead times nos bins
    leadTimes.forEach((leadTime, index) => {
      const binIndex = bins.findIndex(bin => 
        leadTime >= bin.rangeStart && leadTime <= bin.rangeEnd
      );
      if (binIndex >= 0) {
        bins[binIndex].count++;
        bins[binIndex].items.push(filteredData[index]);
      }
    });

    return bins;
  };

  // Cria bins para o histograma baseado na escala selecionada
  const histogramData = useMemo(() => {
    return createBinsByScale(leadTimes, binScale);
  }, [leadTimes, filteredData, binScale]);

  // Função de exportação de imagem
  const chartAreaRef = useRef(null);
  const handleExport = () => {
    if (!chartAreaRef.current) return;
    
    // Track the export action
    if (plausible) {
      plausible.trackExport('png', 'leadtime', 'leadtime-histogram');
      plausible.trackSection('export-png');
    }
    
    toPng(chartAreaRef.current, { backgroundColor: '#fff' })
      .then((dataUrl) => {
        saveAs(dataUrl, 'leadtime-histogram.png');
      })
      .catch(() => {
        alert('Erro ao exportar imagem');
      });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      const isRangeFormat = data.range.includes('-');
      const leadTimeText = isRangeFormat 
        ? `Lead Time: ${data.range} dias` 
        : `Lead Time: ${data.range} ${data.range === '1' ? 'dia' : 'dias'}`;
      
      return (
        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <p className="font-semibold">{leadTimeText}</p>
          <p className="text-blue-600">Quantidade: {data.count} itens</p>
          <p className="text-gray-600 text-sm">
            {Math.round((data.count / leadTimes.length) * 100)}% do total
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={chartRef} className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-1 sm:p-6 mt-8 mb-16 px-0 sm:px-2 flex flex-col items-stretch relative">
      <LeadTimeChartTypeSelector
        selectedType={leadTimeChartType}
        onTypeChange={setLeadTimeChartType}
        onExport={handleExport}
      />
      <div className="flex flex-col lg:flex-row justify-between items-start mb-2 sm:mb-4 gap-2">
        {/* Título removido - agora está no seletor */}
      </div>

      <div className="min-h-[320px] sm:min-h-[400px] max-h-[600px] sm:max-h-[700px] flex flex-col sm:flex-row">
        <div className="flex-1 flex flex-col min-w-0">
          <Filters
            allTypes={allTypes}
            visibleTypes={visibleTypes}
            setVisibleTypes={setVisibleTypes}
            visiblePercentiles={visiblePercentiles}
            togglePercentile={togglePercentile}
          />

          <div className="flex flex-col sm:flex-row gap-2 items-center mb-4 w-full px-1">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              minDate={minDate}
              maxDate={maxDate}
            />
            
            {/* Seletor de escala dos bins */}
            <div className="flex flex-row gap-2 w-full sm:w-auto justify-start sm:justify-end mt-2 sm:mt-0">
              {scaleOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setBinScale(opt.key)}
                  className={`px-3 py-1 rounded-full border text-sm font-medium transition-all ${
                    binScale === opt.key 
                      ? 'bg-blue-600 text-white border-blue-700 scale-105' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-blue-50'
                  }`}
                  type="button"
                  style={{ minWidth: 80 }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-row w-full flex-1 min-h-0 items-stretch">
            <div className="flex-1 min-w-0 flex flex-col h-full">
              <div ref={chartAreaRef} style={{ background: '#fff', borderRadius: 8, padding: 0 }}>
                <ResponsiveContainer width="100%" minWidth={120} minHeight={320}>
                  <BarChart data={histogramData} margin={{ top: 10, right: 10, left: 30, bottom: 10 }}>
                    <XAxis
                      dataKey="range"
                      tick={{ fontSize: isMobile ? 8 : 10, fill: '#374151', fontWeight: 500 }}
                      label={{
                        value: 'Lead Time (dias)',
                        position: 'insideBottom',
                        offset: -5,
                        fontSize: isMobile ? 10 : 13,
                        fill: '#374151',
                        fontWeight: 600,
                      }}
                      angle={binScale === '1em1' ? -45 : 0}
                      textAnchor={binScale === '1em1' ? 'end' : 'middle'}
                      height={binScale === '1em1' ? 60 : 40}
                      interval={binScale === '1em1' ? 0 : 'preserveStartEnd'}
                    />
                    <YAxis
                      tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }}
                      label={{
                        value: 'Quantidade de Itens',
                        angle: -90,
                        position: 'left',
                        fontSize: isMobile ? 10 : 13,
                        fill: '#374151',
                        fontWeight: 600,
                        dx: 0,
                        dy: 0,
                      }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#3B82F6" opacity={0.8} />
                    
                    {/* Linhas de percentis - convertendo valores para posições no eixo X */}
                    {['P50', 'P85', 'P95'].map((key) => {
                      if (!visiblePercentiles[key]) return null;
                      
                      // Encontra o bin que contém o percentil
                      const percentilValue = percentiles[key];
                      const binWithPercentil = histogramData.find(bin => 
                        percentilValue >= bin.rangeStart && percentilValue <= bin.rangeEnd
                      );
                      
                      if (!binWithPercentil) return null;
                      
                      return (
                        <ReferenceLine
                          key={key}
                          x={binWithPercentil.range}
                          stroke={PERCENTILE_COLORS[key]}
                          strokeDasharray="4 4"
                          strokeWidth={3}
                          label={{
                            value: `${key}: ${Math.round(percentiles[key])} dias`,
                            position: 'insideTopRight',
                            fill: PERCENTILE_COLORS[key],
                            fontWeight: 700,
                            fontSize: isMobile ? 10 : 14,
                            dx: -5,
                            dy: 10,
                          }}
                        />
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Histogram;
